import { useState, useEffect } from "react";
import {
  getAllTransactions,
  getTransactionSummary,
} from "../../services/transactionService";
import { exportToExcel, exportToPDF } from "../../utils/exportUtils";
import { toast } from "react-hot-toast";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  FileSpreadsheet,
  FileText,
  Search,
  Filter,
  Calendar as CalendarIcon,
  ArrowUpCircle,
  ArrowDownCircle,
} from "lucide-react";

export default function InventoryDashboard() {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [summary, setSummary] = useState({
    totalInward: 0,
    totalOutward: 0,
    totalInwardPoints: 0,
    totalOutwardPoints: 0,
    inwardCount: 0,
    outwardCount: 0,
    balance: 0,
    balancePoints: 0,
  });

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    filterTransactions();
  }, [transactions, activeTab, searchTerm]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const data = await getAllTransactions();
      setTransactions(data);
      setSummary(getTransactionSummary(data));
    } catch (error) {
      toast.error("Failed to fetch transactions");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterTransactions = () => {
    let filtered = [...transactions];

    // Filter by tab
    if (activeTab === "inward") {
      filtered = filtered.filter((t) => t.type === "inward");
    } else if (activeTab === "outward") {
      filtered = filtered.filter((t) => t.type === "outward");
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((t) => {
        const userName = (t.userName || "").toLowerCase();
        const products = (t.products || [])
          .map((p) => p.productName || "")
          .join(" ")
          .toLowerCase();
        return userName.includes(searchLower) || products.includes(searchLower);
      });
    }

    setFilteredTransactions(filtered);
  };

  const handleExportExcel = () => {
    try {
      exportToExcel(filteredTransactions, `${activeTab}_transactions`);
      toast.success("Excel file downloaded successfully!");
    } catch (error) {
      toast.error("Failed to export Excel");
      console.error("Error:", error);
    }
  };

  const handleExportPDF = () => {
    try {
      exportToPDF(filteredTransactions, `${activeTab}_transactions`, activeTab);
      toast.success("PDF file downloaded successfully!");
    } catch (error) {
      toast.error("Failed to export PDF");
      console.error("Error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Inventory Dashboard</h1>
              <p className="text-sm text-gray-500">Track inward and outward inventory transactions</p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Inward */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-5 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 opacity-80" />
              <span className="text-sm opacity-80">Inward</span>
            </div>
            <div className="text-3xl font-bold mb-1">
              ₹{summary.totalInward.toLocaleString()}
            </div>
            <div className="text-sm opacity-90">
              {Math.round(summary.totalInwardPoints).toLocaleString()} points
            </div>
            <div className="text-xs opacity-75 mt-2">
              {summary.inwardCount} transactions
            </div>
          </div>

          {/* Total Outward */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-5 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingDown className="w-8 h-8 opacity-80" />
              <span className="text-sm opacity-80">Outward</span>
            </div>
            <div className="text-3xl font-bold mb-1">
              ₹{summary.totalOutward.toLocaleString()}
            </div>
            <div className="text-sm opacity-90">
              {Math.round(summary.totalOutwardPoints).toLocaleString()} points
            </div>
            <div className="text-xs opacity-75 mt-2">
              {summary.outwardCount} transactions
            </div>
          </div>

          {/* Balance Amount */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 opacity-80" />
              <span className="text-sm opacity-80">Balance</span>
            </div>
            <div className="text-3xl font-bold mb-1">
              ₹{summary.balance.toLocaleString()}
            </div>
            <div className="text-sm opacity-90">Amount</div>
          </div>

          {/* Balance Points */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Package className="w-8 h-8 opacity-80" />
              <span className="text-sm opacity-80">Balance</span>
            </div>
            <div className="text-3xl font-bold mb-1">
              {Math.round(summary.balancePoints).toLocaleString()}
            </div>
            <div className="text-sm opacity-90">Points</div>
          </div>
        </div>

        {/* Table Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab("all")}
                className={`px-6 py-4 font-medium text-sm transition-colors ${activeTab === "all"
                    ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
              >
                All Transactions ({transactions.length})
              </button>
              <button
                onClick={() => setActiveTab("inward")}
                className={`px-6 py-4 font-medium text-sm transition-colors ${activeTab === "inward"
                    ? "text-green-600 border-b-2 border-green-600 bg-green-50"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
              >
                Inward ({summary.inwardCount})
              </button>
              <button
                onClick={() => setActiveTab("outward")}
                className={`px-6 py-4 font-medium text-sm transition-colors ${activeTab === "outward"
                    ? "text-orange-600 border-b-2 border-orange-600 bg-orange-50"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
              >
                Outward ({summary.outwardCount})
              </button>
            </div>
          </div>

          {/* Table Header with Search and Export */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Transactions</h2>
              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search"
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                  />
                </div>

                {/* Export Buttons */}
                <button
                  onClick={handleExportExcel}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel
                </button>
                <button
                  onClick={handleExportPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  PDF
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Products
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value Points
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-gray-500">Loading transactions...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <Package className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500">No transactions found</p>
                    </td>
                  </tr>
                ) : (
                  filteredTransactions.map((transaction) => {
                    const products = transaction.products || [];
                    const totalQty = products.reduce((sum, p) => sum + (p.quantity || 0), 0);
                    const transactionDate = new Date(transaction.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });

                    return (
                      <tr key={transaction.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{transactionDate}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {transaction.type === "inward" ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <ArrowDownCircle className="w-3 h-3" />
                              Inward
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                              <ArrowUpCircle className="w-3 h-3" />
                              Outward
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">
                            {transaction.userName || "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {products.slice(0, 2).map((product, index) => (
                              <div key={index} className="text-sm text-gray-600">
                                {product.productName} ({product.quantity})
                              </div>
                            ))}
                            {products.length > 2 && (
                              <div className="text-xs text-gray-500">
                                +{products.length - 2} more
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{totalQty}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">
                            ₹{(transaction.totalAmount || 0).toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">
                            {Math.round(transaction.totalValuePoints || 0).toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
